import {
  PaymentType, PayoffMethodsResponse, PayoutMethod, UnavailablePayoutMethod, UnavailablePayoutReason
} from "../../types";
import {missingApiValue} from "./theme";
import {CardDestinationView, PayoutMethodView} from "./types";
import {formatPayoutCentsAmount, formatPayoutFeeLabel, isValidCentsAmount} from "./utils";

const finalPayoutAmountUnavailableMessage = "We couldn't calculate the final payout amount. Please try again.";
const unavailablePayoutAmountLabel = 'Unavailable';
const unavailablePayoutAmountEta = 'Final amount unavailable';
const linkedBankAccountLabel = 'Linked bank account';

const methodUiCopy: Record<PaymentType, { icon: string, subtitle: string, reviewTitle: string, etaWarn?: boolean }> = {
  STANDARD_ACH: {
    icon: 'university', subtitle: 'Direct deposit to your linked bank account.', reviewTitle: 'Standard ACH'
  },
  SAME_DAY_ACH: {
    icon: 'bolt', subtitle: 'Funds arrive today if submitted before 3:45 PM ET.', reviewTitle: 'Same-day ACH',
    etaWarn: true
  },
  CARD: {icon: 'credit-card', subtitle: 'Pushed to your card.', reviewTitle: 'Instant debit'}
};

const unavailableReasonLabel: Record<UnavailablePayoutReason, string> = {
  COMING_SOON: 'Coming soon', KYC_REQUIRED: 'KYC required', NO_PAYOUT_INSTRUMENT: 'No payout instrument',
  REGION_NOT_SUPPORTED: 'Region not supported', AMOUNT_LIMIT_EXCEEDED: 'Amount limit exceeded',
  ACCOUNT_FROZEN: 'Account frozen', COMPLIANCE_HOLD: 'Compliance hold'
};

export type PayoutMethodsPolicyResult = {
  methodRows: PayoutMethodView[], selectableMethodRows: PayoutMethodView[], defaultMethodType?: PaymentType,
  blockingError: string
};

const isPaymentType = (value?: string): value is PaymentType => {
  return value === 'CARD' || value === 'SAME_DAY_ACH' || value === 'STANDARD_ACH';
};

export const buildPayoutDestinationLabel = (methodType?: PaymentType, cardDestination?: CardDestinationView) => {
  if (!methodType) return missingApiValue;
  return methodType === 'CARD' ? cardDestination?.summary || missingApiValue : linkedBankAccountLabel;
};

const hasValidPayoutQuote = (method: PayoutMethod, amountCents: number) => {
  const feeAmountCents = method.feeAmountCents;
  const netAmountCents = method.netAmountCents;
  return isValidCentsAmount(amountCents) && isValidCentsAmount(feeAmountCents) && isValidCentsAmount(netAmountCents) && feeAmountCents + netAmountCents === amountCents;
};

const isPayoutMethodSelectable = (method: PayoutMethod, amountCents: number) => {
  return method.available !== false && hasValidPayoutQuote(method, amountCents);
};

export const buildPayoutMethodView = (method: PayoutMethod, type: PaymentType, amountCents: number): PayoutMethodView => {
  const uiCopy = methodUiCopy[type];
  const title = method.displayName || missingApiValue;
  const eta = method.estimatedDelivery || missingApiValue;
  const reviewTitle = type === 'CARD' || title === missingApiValue ? uiCopy.reviewTitle : title;
  const hasValidQuote = hasValidPayoutQuote(method, amountCents);

  return {
    type, icon: uiCopy.icon, title, subtitle: uiCopy.subtitle, eta: hasValidQuote ? eta : unavailablePayoutAmountEta,
    etaWarn: uiCopy.etaWarn || !hasValidQuote, reviewTitle, reviewEta: eta, reviewSubtitle: `${reviewTitle} · ${eta}`,
    feeLabel: hasValidQuote ? formatPayoutFeeLabel(method.feeAmountCents) : unavailablePayoutAmountLabel,
    netAmountLabel: hasValidQuote ? formatPayoutCentsAmount(method.netAmountCents) : unavailablePayoutAmountLabel,
    recommended: hasValidQuote && !!method.recommended, available: isPayoutMethodSelectable(method, amountCents),
    showAmounts: hasValidQuote
  };
};

export const buildUnavailablePayoutMethodView = (method: UnavailablePayoutMethod, type: PaymentType): PayoutMethodView => {
  const uiCopy = methodUiCopy[type];
  const eta = method.reason ? unavailableReasonLabel[method.reason] || method.reason : missingApiValue;

  return {
    type, icon: uiCopy.icon, title: uiCopy.reviewTitle, subtitle: uiCopy.subtitle, eta, etaWarn: true,
    reviewTitle: uiCopy.reviewTitle, reviewEta: eta, reviewSubtitle: `${uiCopy.reviewTitle} · ${eta}`,
    feeLabel: missingApiValue, netAmountLabel: missingApiValue, recommended: false, available: false,
    showAmounts: false
  };
};

export const applyPayoutMethodsPolicy = (response: PayoffMethodsResponse, amountCents: number): PayoutMethodsPolicyResult => {
  const backendAvailableMethods = (response.methods || []).reduce<Array<{ method: PayoutMethod, type: PaymentType }>>(
    (methods, method) => {
      if (isPaymentType(method.type)) methods.push({method, type: method.type});
      return methods;
    }, []
  );
  const availableMethodRows = backendAvailableMethods.map(({method, type}) => {
    return buildPayoutMethodView(method, type, amountCents);
  });
  const unavailableMethodRows = (response.unavailable || []).reduce<PayoutMethodView[]>((rows, method) => {
    if (isPaymentType(method.type)) rows.push(buildUnavailablePayoutMethodView(method, method.type));
    return rows;
  }, []);
  const selectableMethodRows = availableMethodRows.filter(method => method.available);
  const backendAvailableMethodHasInvalidQuote = backendAvailableMethods.some(({method}) => method.available !== false && !hasValidPayoutQuote(method, amountCents));
  const requestedDefaultMethod = isPaymentType(response.defaultMethod) ? selectableMethodRows.find(method => method.type === response.defaultMethod)?.type : undefined;

  return {
    methodRows: [...availableMethodRows, ...unavailableMethodRows], selectableMethodRows,
    defaultMethodType: requestedDefaultMethod || selectableMethodRows[0]?.type,
    blockingError: !selectableMethodRows.length && backendAvailableMethodHasInvalidQuote ? finalPayoutAmountUnavailableMessage : ''
  };
};
